<template>
  <div class="directionality-container">
    <div class="chart-index">
      <div class="chart-header">
        <div class="header-content">
          <h3 class="chart-title">Mapa de direccionalidad</h3>
          <button class="info-toggle" @click="toggleInfoSection" aria-label="Información sobre direccionalidad">
            <span class="material-icons">help_outline</span>
          </button>
        </div>
        
        <!-- Info Section -->
        <div v-if="showInfoSection" class="info-section">
          <div class="info-card">
            <div class="info-header">
              <span class="material-icons">info</span>
              <h4>¿Qué es la direccionalidad?</h4>
            </div>
            <div class="info-content">
              <p>La direccionalidad mide el porcentaje de valores que están en tendencia alcista en diferentes horizontes temporales:</p>
              <div class="indicators-explanation">
                <div class="indicator-item">
                  <div class="indicator-badge corto">Corto</div>
                  <span>Últimas 5 sesiones (~1 semana)</span>
                </div>
                <div class="indicator-item">
                  <div class="indicator-badge medio">Medio</div>
                  <span>Últimas 70 sesiones (~3 meses)</span>
                </div>
                <div class="indicator-item">
                  <div class="indicator-badge largo">Largo</div>
                  <span>Últimas 220 sesiones (~1 año)</span>
                </div>
              </div>
              <div class="explanation-note">
                <span class="material-icons">lightbulb</span>
                <span>Un valor alto indica que la mayoría de valores del índice están en tendencia alcista</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="visualization-container">
        <div class="map-container">
          <div class="directionality-overlay"></div>
          <div class="map-wrapper">
            <highcharts ref="mapChart" :constructor-type="'mapChart'" :options="mapOptions"></highcharts>
          </div>
          
          <div class="controls-container">
            <div class="time-controls">
              <div class="time-buttons">
                <button 
                  v-for="(button, index) in timeButtons" 
                  :key="index"
                  :id="button.id" 
                  :class="['time-btn', activeButton === button.id ? 'active' : '']"
                  @click="setActiveButton(button.id)">
                  <span class="btn-label">{{ button.label }}</span>
                  <span class="btn-subtitle">{{ button.subtitle }}</span>
                </button>
              </div>
            </div>
            
            <div class="zoom-controls">
              <button class="zoom-btn" @click="zoomIn" aria-label="Zoom in">
                <span class="material-icons">add</span>
              </button>
              <button class="zoom-btn" @click="zoomOut" aria-label="Zoom out">
                <span class="material-icons">remove</span>
              </button>
            </div>
            
            <div class="tooltip-container">
              <div v-if="tooltipData" class="tooltip-active">
                <div class="tooltip-flag" v-if="tooltipData.flag">
                  <span class="f32"><span :class="'flag ' + tooltipData.flag"></span></span>
                </div>
                <div class="tooltip-content">
                  <div class="tooltip-country">{{ tooltipData.name }}</div>
                  <div class="tooltip-value">{{ tooltipData.value }}%</div>
                </div>
              </div>
              <div v-else class="tooltip-default">
                <div class="tooltip-icon">
                  <span class="material-icons">public</span>
                </div>
                <div class="tooltip-content">
                  <div class="tooltip-label">Ponga el cursor sobre un país</div>
                </div>
              </div>
            </div>
            
            <div class="legend-container">
              <div class="legend-items">
                <div 
                  v-for="(range, index) in legendRanges" 
                  :key="index" 
                  class="legend-item"
                  :class="{ 'legend-item-disabled': !range.visible }"
                  @click="toggleLegendRange(index)"
                >
                  <div class="legend-color" :style="{ backgroundColor: range.color, opacity: range.visible ? 1 : 0.3 }"></div>
                  <div class="legend-label">{{ range.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- New Countries Ranking Visualization -->
        <div class="countries-ranking-container">
          <div class="countries-list">
            <div 
              v-for="country in sortedCountries" 
              :key="country.code"
              class="country-item"
              @mouseover="updateTooltip({name: country.name, value: country.value, flag: country.code.toLowerCase()})"
              @mouseleave="clearTooltip"
            >
              <div class="country-name">{{ country.name }}</div>
              <div class="country-value-container">
                <div 
                  class="country-value-bar" 
                  :style="{ width: country.value + '%' }"
                  :class="getValueColorClass(country.value)"
                >
                  <span class="country-value-percent">{{ Math.round(country.value) }}%</span>
                </div>
                <span class="country-value-text">{{ Math.round(country.value) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <TableIndex :indices_data="indices_data.table_index"/>
  </div>
</template>

<script>
import TableIndex from '@/features/directionality/components/TableIndex.vue';
import Highcharts from "highcharts";
import WorldMap from '@/features/directionality/data/world';

// Initialize Highcharts world map
WorldMap(Highcharts);

export default {
  name: 'Indices',
  components: {
    TableIndex,
  },
  props: {
    best_country: Object,
    indices_data: Object,
    best_sector: Object,
    sectors_data: Object,
  },
  data() {
    return {
      activeButton: 'index_map_1m',
      tooltipData: null,
      timeButtons: [
        { id: 'index_map_1m', label: 'corto', subtitle: '1 semana', class: 'btn-short', seriesName: 'Direccionalidad a corto plazo' },
        { id: 'index_map_3m', label: 'medio', subtitle: '3 meses', class: 'btn-middle', seriesName: 'Direccionalidad a medio plazo' },
        { id: 'index_map_1y', label: 'largo', subtitle: '1 año', class: 'btn-large', seriesName: 'Direccionalidad a largo plazo' }
      ],
      legendRanges: [
        { 
          color: '#D90033', 
          label: '0-20%', 
          visible: true,
          min: 0,
          max: 20
        },
        { 
          color: '#D96900', 
          label: '20-40%', 
          visible: true,
          min: 20,
          max: 40
        },
        { 
          color: '#D2D900', 
          label: '40-60%', 
          visible: true,
          min: 40,
          max: 60
        },
        { 
          color: '#00D953', 
          label: '60-80%', 
          visible: true,
          min: 60,
          max: 80
        },
        { 
          color: '#00add9', 
          label: '80-100%', 
          visible: true,
          min: 80,
          max: 100
        }
      ],
      mapOptions: null,
      originalMapData: {},
      chartInstance: null,
      countryTranslations: {
        'United States': 'Estados Unidos',
        'USA': 'Estados Unidos',
        'US': 'Estados Unidos',
        'United Kingdom': 'Reino Unido',
        'UK': 'Reino Unido',
        'GB': 'Reino Unido',
        'Germany': 'Alemania',
        'DE': 'Alemania',
        'France': 'Francia',
        'FR': 'Francia',
        'Spain': 'España',
        'ES': 'España',
        'Italy': 'Italia',
        'IT': 'Italia',
        'Netherlands': 'Países Bajos',
        'NL': 'Países Bajos',
        'Canada': 'Canadá',
        'CA': 'Canadá',
        'Brazil': 'Brasil',
        'BR': 'Brasil',
        'Mexico': 'México',
        'MX': 'México',
        'Russia': 'Rusia',
        'RU': 'Rusia',
        'China': 'China',
        'CN': 'China',
        'Japan': 'Japón',
        'JP': 'Japón',
        'India': 'India',
        'IN': 'India',
        'Australia': 'Australia',
        'AU': 'Australia',
        'South Korea': 'Corea del Sur',
        'KR': 'Corea del Sur',
        'South Africa': 'Sudáfrica',
        'ZA': 'Sudáfrica',
        'Switzerland': 'Suiza',
        'CH': 'Suiza',
        'Sweden': 'Suecia',
        'SE': 'Suecia',
        'Norway': 'Noruega',
        'NO': 'Noruega',
        'Denmark': 'Dinamarca',
        'DK': 'Dinamarca',
        'Finland': 'Finlandia',
        'FI': 'Finlandia',
        'Belgium': 'Bélgica',
        'BE': 'Bélgica',
        'Austria': 'Austria',
        'AT': 'Austria',
        'Poland': 'Polonia',
        'PL': 'Polonia',
        'Portugal': 'Portugal',
        'PT': 'Portugal',
        'Greece': 'Grecia',
        'GR': 'Grecia',
        'Ireland': 'Irlanda',
        'IE': 'Irlanda',
        'New Zealand': 'Nueva Zelanda',
        'NZ': 'Nueva Zelanda',
        'Singapore': 'Singapur',
        'SG': 'Singapur',
        'Hong Kong': 'Hong Kong',
        'HK': 'Hong Kong',
        'Taiwan': 'Taiwán',
        'TW': 'Taiwán',
        'Israel': 'Israel',
        'IL': 'Israel',
        'Turkey': 'Turquía',
        'TR': 'Turquía',
        'Indonesia': 'Indonesia',
        'ID': 'Indonesia',
        'Thailand': 'Tailandia',
        'TH': 'Tailandia',
        'Malaysia': 'Malasia',
        'MY': 'Malasia',
        'Philippines': 'Filipinas',
        'PH': 'Filipinas',
        'Hungary': 'Hungría',
        'HU': 'Hungría',
        'Czech Republic': 'República Checa',
        'CZ': 'República Checa',
        'Chile': 'Chile',
        'CL': 'Chile',
        'Argentina': 'Argentina',
        'AR': 'Argentina',
        'Colombia': 'Colombia',
        'CO': 'Colombia',
        'Peru': 'Perú',
        'PE': 'Perú',
        'GB': 'Gran Bretaña',
        'England': 'Gran Bretaña',
        'SG': 'Singapur',
        'Singapoore': 'Singapur',
        'XIST': 'Estanbul',
        'Istanbul': 'Estanbul'
      },
      showInfoSection: false
    };
  },
  computed: {
    sortedCountries() {
      const data = this.indices_data[this.activeButton];
      if (!data) return [];
      
      return Object.values(data)
        .map(country => ({
          name: this.getTranslatedCountryName(country.name),
          code: country.code,
          value: country.value
        }))
        .sort((a, b) => b.value - a.value);
    }
  },
  created() {
    // Store a reference to the Vue component for use in Highcharts callbacks
    const component = this;
    
    this.mapOptions = {
      chart: {
        map: 'world',
        backgroundColor: 'var(--color-background)',
        animation: false,
        events: {
          load: function() {
            // Store the chart instance for later use
            component.chartInstance = this;
            
            // Set a fixed highlight color (matching the button highlight color)
            const highlightColor = '#545E7D'; // Default blue highlight color
            
            // Apply the highlight color to the hover state
            this.series.forEach(function(series) {
              series.update({
                states: {
                  hover: {
                    color: highlightColor,
                    animation: false
                  }
                }
              }, false);
            });
            
            this.redraw();
          }
        }
      },
      title: {
        text: ''
      },
      subtitle: {
        text: ''
      },
      legend: {
        enabled: false // Disable the built-in legend
      },
      mapNavigation: {
        enabled: false // Disable built-in navigation buttons
      },
      colorAxis: {
        min: 1,
        max: 100,
        type: 'linear',
        dataClassColor: 'category',
        dataClasses: [
          { to: 20 },
          { from: 20, to: 40 },
          { from: 40, to: 60 },
          { from: 60, to: 80 },
          { from: 80 }
        ],
      },
      colors: [
        '#D90033', 
        '#D96900', 
        '#D2D900',
        '#00D953', 
        '#00add9'
      ],
      tooltip: {
        enabled: false // Disable the built-in tooltip
      },
      plotOptions: {
        series: {
          animation: false,
          states: {
            hover: {
              animation: false
            }
          },
          point: {
            events: {
              // Handle point hover to update our custom tooltip
              mouseOver: function() {
                const point = this;
                if (point.name && point.value !== undefined) {
                  // Use the stored component reference
                  component.updateTooltip({
                    name: point.name,
                    value: point.value,
                    flag: point.properties && point.properties['hc-key']
                  });
                }
              },
              mouseOut: function() {
                // Clear tooltip when mouse leaves point
                component.clearTooltip();
              }
            }
          }
        }
      },
      series: [{
        name: 'Direccionalidad a corto plazo',
        animation: false,
        dataLabels: {
          enabled: false,
          format: '{point.name}'
        },
        allAreas: true,
        data: [],
        borderColor: 'var(--color-outline)',
        borderWidth: 0.5,
        nullColor: 'var(--color-active-surface)'
      }]
    };
  },
  mounted() {
    // Initial data load will happen after chart is created
    this.$nextTick(() => {
      this.updateSeriesName();
      this.updateMapData();
    });
  },
  watch: {
    activeButton() {
      this.updateSeriesName();
      this.updateMapData();
    },
    indices_data: {
      handler() {
        if (this.indices_data && this.indices_data[this.activeButton]) {
          this.updateMapData();
        }
      },
      deep: true // Watch for nested changes in indices_data
    }
  },
  methods: {
    setActiveButton(buttonId) {
      this.activeButton = buttonId;
    },
    updateMapData() {
      // Make sure the chart is available
      if (!this.chartInstance) {
        const chart = this.$refs.mapChart && this.$refs.mapChart.chart;
        if (chart) {
          this.chartInstance = chart;
        } else {
          console.error('Chart not available');
          return;
        }
      }
      
      // Get the data for the current time period
      const allData = this.parseCountries();
      
      // Store a copy in our originalMapData object, indexed by the active button
      this.originalMapData[this.activeButton] = [...allData];
      
      // Apply filters based on visible legend ranges
      this.filterMapDataByVisibleRanges();
    },
    updateSeriesName() {
      if (!this.chartInstance) return;
      
      const activeTimeButton = this.timeButtons.find(button => button.id === this.activeButton);
      if (activeTimeButton) {
        // Update the series name
        this.chartInstance.series[0].update({
          name: activeTimeButton.seriesName
        }, false);
      }
    },
    parseCountries() {
      const countryMaps = [];
      const data = this.indices_data[this.activeButton];
      
      if (!data) return countryMaps;
      
      Object.entries(data).forEach((country) => {
        countryMaps.push([country[1].code.toLowerCase(), country[1].value]);
      });
      
      return countryMaps;
    },
    zoomIn() {
      if (this.chartInstance) {
        this.chartInstance.mapZoom(0.5);
      }
    },
    zoomOut() {
      if (this.chartInstance) {
        this.chartInstance.mapZoom(2);
      }
    },
    updateTooltip(data) {
      // Translate country name if available
      if (data && data.name) {
        data.name = this.getTranslatedCountryName(data.name);
      }
      this.tooltipData = data;
    },
    clearTooltip() {
      this.tooltipData = null;
    },
    toggleLegendRange(index) {
      // Toggle visibility of the selected range
      this.legendRanges[index].visible = !this.legendRanges[index].visible;
      
      // Apply the filter to the map data
      this.filterMapDataByVisibleRanges();
    },
    filterMapDataByVisibleRanges() {
      if (!this.chartInstance) return;
      
      // Get the original data for the current time period
      const allData = this.originalMapData[this.activeButton];
      if (!allData || !allData.length) {
        return;
      }
      
      // Filter the data based on visible ranges
      const filteredData = allData.filter(item => {
        const value = item[1];
        // Check if this value falls within any visible range
        return this.legendRanges.some(range => 
          range.visible && value >= range.min && value <= range.max
        );
      });
      
      // Update the chart with the filtered data
      this.chartInstance.series[0].setData(filteredData, true, false);
    },
    getValueColorClass(value) {
      if (value < 20) return 'color-red';
      if (value < 40) return 'color-orange';
      if (value < 60) return 'color-yellow';
      if (value < 80) return 'color-green';
      return 'color-blue';
    },
    getTranslatedCountryName(name) {
      return this.countryTranslations[name] || name;
    },
    toggleInfoSection() {
      this.showInfoSection = !this.showInfoSection;
    }
  }
};
</script>

<style lang="scss" scoped>
.directionality-container {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.chart-index {
  position: relative;
  margin-bottom: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }
}

.chart-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-outline);
  background-color: var(--color-background);
  display: flex;
  justify-content: space-between;
  flex-direction: column;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.chart-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  letter-spacing: -0.25px;
}

.info-toggle {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: var(--color-text-secondary);
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: var(--color-active-surface);
    color: var(--color-primary);
    transform: scale(1.1);
  }
  
  .material-icons {
    font-size: 20px;
  }
}

.visualization-container {
  display: flex;
  flex-direction: row;
  
  @media (max-width: 1200px) {
    flex-direction: column;
  }
}

.map-container {
  padding: 24px;
  position: relative;
  flex: 3;
}

.map-wrapper {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--color-outline);
  background-color: var(--color-background);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.controls-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 28px 0 16px;
  gap: 20px;
  flex-wrap: wrap;
}

.time-controls {
  display: flex;
  justify-content: center;
}

.time-buttons {
  display: inline-flex;
  background-color: var(--color-background);
  border-radius: 28px;
  padding: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--color-outline);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }
}

.time-btn {
  background-color: transparent;
  color: var(--color-text-secondary);
  border: none;
  border-radius: 24px;
  padding: 10px 20px;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.25px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120%;
    height: 0;
    padding-bottom: 120%;
    background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%);
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
    transition: opacity 0.5s ease, transform 0.5s ease;
  }
  
  &:hover:not(.active) {
    background-color: var(--color-active-surface);
    color: var(--color-text-primary);
    transform: translateY(-1px);
    
    &::after {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  &:active:not(.active) {
    transform: translateY(1px);
  }
  
  &.active {
    background-color: #00add9;
    color: white;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    
    &:hover {
      background-color: #2980b9;
    }
  }
}

.btn-label {
  font-size: 14px;
  font-weight: 500;
  text-transform: capitalize;
}

.btn-subtitle {
  font-size: 11px;
  font-weight: 400;
  opacity: 0.8;
}

.zoom-controls {
  display: inline-flex;
  background-color: var(--color-background);
  border-radius: 24px;
  padding: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--color-outline);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }
}

.zoom-btn {
  background-color: transparent;
  color: var(--color-text-secondary);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120%;
    height: 0;
    padding-bottom: 120%;
    background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%);
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
    transition: opacity 0.5s ease, transform 0.5s ease;
  }
  
  &:hover {
    background-color: var(--color-active-surface);
    color: var(--color-text-primary);
    transform: translateY(-1px);
    
    &::after {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  &:active {
    background-color: #00add9;
    color: white;
    transform: translateY(1px);
  }
  
  .material-icons {
    font-size: 20px;
    transition: transform 0.2s ease;
  }
  
  &:hover .material-icons {
    transform: scale(1.1);
  }
}

.tooltip-container {
  display: flex;
  align-items: center;
  background-color: var(--color-background);
  border-radius: 24px;
  padding: 8px 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--color-outline);
  width: 280px;
  height: 48px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
}

.tooltip-active, .tooltip-default {
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
}

.tooltip-flag {
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tooltip-icon {
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  
  .material-icons {
    font-size: 24px;
    opacity: 0.8;
  }
}

.tooltip-content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
}

.tooltip-country {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 14px;
  letter-spacing: -0.25px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tooltip-value {
  font-weight: 700;
  color: #00add9;
  font-size: 18px;
  letter-spacing: -0.25px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tooltip-label {
  font-weight: 500;
  color: var(--color-text-secondary);
  font-size: 14px;
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.legend-container {
  display: flex;
  background-color: var(--color-background);
  border-radius: 24px;
  padding: 8px 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--color-outline);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
}

.legend-items {
  display: flex;
  align-items: center;
  gap: 12px;
}

.legend-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 6px 8px;
  border-radius: 6px;
  
  &:hover {
    background-color: var(--color-active-surface);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(1px);
  }
}

.legend-item-disabled .legend-label {
  color: var(--color-text-inactive);
  text-decoration: line-through;
}

.legend-color {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  
  .legend-item:hover & {
    transform: scale(1.1);
  }
}

.legend-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-secondary);
  transition: color 0.3s ease;
  
  .legend-item:hover & {
    color: var(--color-text-primary);
  }
}

.table-container {
  width: 100%;
  margin-bottom: 40px;
}

highcharts {
  filter: none;
}

.directionality-overlay {
  display: none;
}

/* New Countries Ranking Styles */
.countries-ranking-container {
  flex: 1;
  border-left: 1px solid var(--color-outline);
  min-width: 280px;
  position: relative;
  
  @media (max-width: 1200px) {
    border-left: none;
    border-top: 1px solid var(--color-outline);
    height: 400px;
    display: flex;
    flex-direction: column;
  }
}

.ranking-header {
  padding: 16px;
  border-bottom: 1px solid var(--color-outline);
  background-color: var(--color-background);
}

.ranking-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  letter-spacing: -0.25px;
}

.countries-list {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;

  @media (max-width: 1200px) {
    position: static;
    flex: 1;
    max-height: 400px;
  }
}

.country-item {
  display: flex;
  flex-direction: column;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: var(--color-active-surface);
  }
}

.country-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.country-value-container {
  height: 20px;
  position: relative;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.country-value-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  
  &.color-red {
    background-color: #D90033;
  }
  
  &.color-orange {
    background-color: #D96900;
  }
  
  &.color-yellow {
    background-color: #D2D900;
  }
  
  &.color-green {
    background-color: #00D953;
  }
  
  &.color-blue {
    background-color: #00add9;
  }
}

.country-value-percent {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.country-value-text {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-primary);
  display: none;
}

/* Override Highcharts styles */
:deep(.highcharts-zoom-in), :deep(.highcharts-zoom-out) {
  fill: var(--color-background) !important;
  stroke: var(--color-outline) !important;
  stroke-width: 1px !important;
  rx: 4px !important;
  ry: 4px !important;
}

:deep(.highcharts-zoom-in text), :deep(.highcharts-zoom-out text) {
  fill: var(--color-text-primary) !important;
}

:deep(.highcharts-zoom-in:hover), :deep(.highcharts-zoom-out:hover) {
  fill: var(--color-active-surface) !important;
}

:deep(.highcharts-zoom-in:hover text), :deep(.highcharts-zoom-out:hover text) {
  fill: #d3eaf0 !important;
}

/* Improve map appearance */
:deep(.highcharts-map-series .highcharts-point) {
  stroke-width: 0.5px;
  transition: fill 0.3s ease, stroke-width 0.3s ease;
}

:deep(.highcharts-map-series .highcharts-point:hover) {
  stroke-width: 1px;
  stroke: #fff;
}

/* Info Section Styles */
.info-section {
  margin-top: 20px;
  border-top: 1px solid var(--color-outline);
  padding-top: 20px;
}

.info-card {
  background: linear-gradient(135deg, var(--color-surface) 0%, rgba(255,255,255,0.8) 100%);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--color-outline);
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.info-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.info-header .material-icons {
  color: var(--color-primary);
  font-size: 20px;
}

.info-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.info-content p {
  margin: 0 0 16px 0;
  color: var(--color-text-secondary);
  line-height: 1.5;
  font-size: 14px;
}

.indicators-explanation {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.indicator-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.indicator-badge {
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: white;
}

.indicator-badge.corto {
  background: linear-gradient(135deg, #ff6b6b, #ff5252);
}

.indicator-badge.medio {
  background: linear-gradient(135deg, #ffd93d, #ffb74d);
  color: var(--color-text-primary);
}

.indicator-badge.largo {
  background: linear-gradient(135deg, #6bcf7f, #4caf50);
}

.indicator-item span {
  color: var(--color-text-secondary);
  font-size: 12px;
}

.explanation-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: rgba(0, 173, 217, 0.1);
  border-radius: 8px;
  border-left: 4px solid #00add9;
}

.explanation-note .material-icons {
  color: #00add9;
  font-size: 16px;
}

.explanation-note span:last-child {
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 500;
}
</style>
